import { ApiError, CancelablePromise, CancelError } from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ReloadableResultStatus } from '../api/resultStatus';

export interface UseApiService<T, E> {
  result: ReloadableResultStatus<T, E>;
  refetch: () => void;
  cancel: () => void;
}

/**
 * Should be kept in sync with the copy of `recruiter-ui`'s `useApiService`
 * hook to allow both to reference a single `:packages/react-hooks` or similar
 *
 * @see {@link :packages/recruiter-ui/app/hooks/useApiService.ts}
 */
export const useApiService = <T, E = unknown>(
  // This service should be memoized before passed in, for example by using `useCallback`.
  service: () => CancelablePromise<T>,
): UseApiService<T, E> => {
  const [result, setResult] = useState<ReloadableResultStatus<T, E>>({
    status: 'loading',
    isLoading: true,
    isError: false,
  });
  const [queryKey, setQueryKey] = useState(false);
  const promiseRef = useRef<CancelablePromise<T> | null>(null);

  const setLoadingOrReloading = useCallback(() => {
    setResult(currentResult => {
      // Has loaded once succesfully and has valid cached data
      if (currentResult.status === 'ready') {
        return {
          status: 'reloading',
          data: currentResult.data,
          isLoading: true,
          isError: false,
        };
      }
      if (currentResult.status !== 'loading') {
        return {
          status: 'loading',
          isLoading: true,
          isError: false,
        };
      }
      return currentResult;
    });
  }, []);

  const cancel = useCallback(() => {
    if (promiseRef.current) {
      promiseRef.current.cancel();
      promiseRef.current = null;
    }
  }, []);

  const refetch = useCallback(() => {
    setQueryKey(currentKey => !currentKey);
  }, []);

  useEffect(() => {
    setLoadingOrReloading();
    const promise = service();
    promiseRef.current = promise;

    promise
      .then(data => {
        setResult({ status: 'ready', data, isLoading: false, isError: false });
        promiseRef.current = null;
      })
      .catch((e: Error) => {
        if (e instanceof CancelError) {
          return;
        }
        if (e instanceof ApiError) {
          const body = e.body as unknown;
          const statusCode = e.status;
          const errorPayload: Record<string, unknown> =
            body !== null && typeof body === 'object' && !Array.isArray(body)
              ? { ...(body as Record<string, unknown>), statusCode }
              : {
                  statusCode,
                  ...(body !== undefined && body !== null && { body }),
                };
          setResult({
            status: 'error',
            isLoading: false,
            isError: true,
            error: errorPayload as E,
          });
          promiseRef.current = null;
          return;
        }
        setResult({ status: 'error', isLoading: false, isError: true });
        promiseRef.current = null;
      });
    return () => promise.cancel();
  }, [service, queryKey, setLoadingOrReloading]);

  return { result, refetch, cancel };
};
